/**
 * Deterministic Home workspace derivation from primarySeed.
 *
 * Uses HKDF (RFC 5869) to derive a stable blobId and workspace-secret-v2
 * from the user's identity seed. This ensures that all devices
 * sharing the same primarySeed point to the same Home workspace
 * without explicit "create Home" coordination.
 *
 * Spec §8.9: deterministic blobs MUST use full workspace-secret-v2
 * including readerSeed, with a dedicated HKDF info string.
 * C# side MUST use identical info strings for cross-platform compat.
 */

import { hkdf } from '@noble/hashes/hkdf'
import { sha256 } from '@noble/hashes/sha256'

// ── HKDF context strings (domain separation) ────────────────────
const HOME_BLOB_ID_INFO = 'secureblob:home-workspace-v1:blobid'
const HOME_READ_KEY_INFO = 'secureblob:home-workspace-v1:readkey'
const HOME_READER_SEED_INFO = 'secureblob:home-workspace-v2:readerseed'
const HOME_WRITE_SEED_INFO = 'secureblob:home-workspace-v1:writeseed'
const HKDF_SALT = new Uint8Array(0) // no salt — primarySeed has enough entropy

// ── Helpers ──────────────────────────────────────────────────────

function toBase64Url(bytes: Uint8Array): string {
    let binary = ''
    bytes.forEach((byte) => { binary += String.fromCharCode(byte) })
    const b64 = btoa(binary)
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function fromBase64Url(value: string): Uint8Array {
    const b64 = value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4)
    const binary = atob(b64)
    const out = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
    return out
}

/** Canonical JSON stringify (sorted keys, no undefined values). */
function canonicalJsonStringify(value: unknown): string {
    if (value === null) return 'null'
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return JSON.stringify(value)
    }
    if (Array.isArray(value)) {
        return `[${value.map(item => canonicalJsonStringify(item)).join(',')}]`
    }
    if (typeof value !== 'object') {
        throw new TypeError('Unsupported value in canonical JSON serialization')
    }
    const entries = Object.entries(value as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0)
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJsonStringify(v)}`).join(',')}}`
}

// ── Public API ───────────────────────────────────────────────────

export interface DerivedHomeWorkspace {
    /** Deterministic blobId (base64url, 16 bytes → 22 chars). */
    blobId: string
    /**
     * Encoded workspace-secret-v2 (Spec §4.1).
     * base64url(UTF8(canonical_json({ v:2, type:"workspace-secret", blobId, readKey, readerSeed, writerSeed }))).
     */
    workspaceSecret: string
}

/**
 * Derive a deterministic Home workspace blobId + workspace-secret-v2
 * from the user's primarySeed.
 *
 * @param primarySeedB64 - base64url-encoded 32-byte primary seed
 * @returns Deterministic blobId and workspace-secret-v2
 */
export function deriveHomeWorkspace(primarySeedB64: string): DerivedHomeWorkspace {
    const seed = fromBase64Url(primarySeedB64)
    if (seed.length !== 32) {
        throw new Error(`deriveHomeWorkspace: expected 32-byte seed, got ${seed.length}`)
    }

    const enc = new TextEncoder()

    // Derive 16 bytes → blobId
    const blobIdBytes = hkdf(sha256, seed, HKDF_SALT, enc.encode(HOME_BLOB_ID_INFO), 16)
    const blobId = toBase64Url(blobIdBytes)

    // Derive 32 bytes → read key
    const readKeyBytes = hkdf(sha256, seed, HKDF_SALT, enc.encode(HOME_READ_KEY_INFO), 32)
    const readKey = toBase64Url(readKeyBytes)

    // Derive 32 bytes → reader seed (Spec §2.2: own Ed25519 identity for read auth)
    const readerSeedBytes = hkdf(sha256, seed, HKDF_SALT, enc.encode(HOME_READER_SEED_INFO), 32)
    const readerSeed = toBase64Url(readerSeedBytes)

    // Derive 32 bytes → write seed
    const writeSeedBytes = hkdf(sha256, seed, HKDF_SALT, enc.encode(HOME_WRITE_SEED_INFO), 32)
    const writeSeed = toBase64Url(writeSeedBytes)

    // Build workspace-secret-v2 (Spec §4.1)
    const secret = { v: 2, type: 'workspace-secret', blobId, readKey, readerSeed, writerSeed: writeSeed } as const
    const secretJson = canonicalJsonStringify(secret)
    const workspaceSecret = toBase64Url(new TextEncoder().encode(secretJson))

    return { blobId, workspaceSecret }
}
